import { Table, Row, Modal, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import classNames from 'classnames';
import React from 'react';
import Highlighter from 'react-highlight-words';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useSortData } from '.';
import { EInvoiceButton, IconButton } from '../../components/button';
import { ICSort, ICAdd, ICNoneData } from '../../icons';
import { ICMaterialSymbol } from '../../icons/material-symbol';
import { TenantForm } from './create';
import { useNotification } from '../../hooks/useNotification';
import { setLoading } from '../../redux/slices/appInfo';
import {
    Direction,
    IPagination,
    SearchQueryParams,
} from '../../interface/paging';
import { removeUndefinedAttribute } from '../../utils';
import debouce from 'lodash/debounce';
import { useScreenSize } from '../../hooks/useScreenSize';
import { TTenantRequest, TTenants } from './type';
import { EInvoicePagination } from '../../components/pagination';
import { createTenant, getTenantDetail, updateTenant } from '../../api/setting/tenants';
import { EcoScrollTable } from '../components/scroll-table';

const ActionMenu = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    cursor: pointer;
    padding-right: 16px;
    &:hover {
        background-color: #f7f8fa;
    }
    button {
        padding: 0 !important;
    }
`;

const WrapperTable = styled.div`
    .ant-table-thead > tr > th {
        background-color: white;
    }
    .data-row:nth-child(odd) {
        background-color: #f7f8fa;
    }
    .ant-table-column-sorters .ant-table-column-sorter-inner {
        .anticon {
            display: none;
        }
    }
`;

const WrapperInput = styled.div`
    @media (max-width: 768px) {
    .ant-input-group-wrapper {
        width: 100% !important;
    }
`;

const WrapperButton = styled.div`
    @media (max-width: 768px) {
        width: 100%;
        .ant-btn {
            width: 100% !important;
        }
    }
`;

const TitleRenderer: React.FC<{ title: string }> = ({ title }) => {
    const keyword = React.useContext(KeywordContext);

    return (
        <Highlighter
            highlightClassName="text-highlight"
            searchWords={[`${keyword || ''}`]}
            autoEscape={true}
            className="text-body text-high-em"
            textToHighlight={title || ''}
        />
    );
};
type SortableColumnProps = {
    title: string;
    order?: SortOrder;
    name: string;
};
const SortableColumn: React.FC<SortableColumnProps> = ({
    title,
    order,
    name,
}) => {
    const { setSortData } = useSortData();
    return (
        <div
            className="flex space-x-3 items-center justify-between select-none group"
            onClick={() => {
                setSortData({
                    field: order === 'descend' ? 'createdDate' : name,
                    order:
                        order === 'descend' ? 'desc' : !order ? 'asc' : 'desc',
                });
            }}
        >
            <span className="text-body-bold text-high-em">
                {title as string}
            </span>
            <ICSort order={order} />
        </div>
    );
};

type TenantsPageListProps = {
    tenantList: TTenants[];
    setTenantList: (tenantServiceList: TTenants[]) => void;
    keyword?: string;
    pagination?: IPagination;
    onChangePaging: ({ page }: { page: number }) => void;
    onReload: () => void;
    filterParams: SearchQueryParams;
    onFilterData: (payload: SearchQueryParams) => void;
};
const KeywordContext = React.createContext<string>('');

export const TenantsPageList: React.FC<TenantsPageListProps> = ({
    tenantList,
    setTenantList,
    keyword,
    pagination,
    onChangePaging,
    onReload,
    filterParams,
    onFilterData,
}) => {
    const size = useScreenSize();
    const mobileMode = size === 'xs' || size === 'sm';
    const dispatch = useDispatch();
    const { showSuccess, showError } = useNotification();
    const onChangePage = (page: number) => {
        onChangePaging({ page });
    };
    const [showTenantDialog, setShowTenantDialog] = React.useState(false);
    const [tenantSelected, setTenantSelected] = React.useState<
        TTenants | undefined
    >(undefined);
    const [form] = useForm();

    // handle search keyword
    const [searchWord, setSearchWord] = React.useState<string | undefined>('');

    const onSearchData = React.useCallback(
        async (searchWord: string | undefined, searchColumnTemp?: string) => {
            try {
                const queryParams: SearchQueryParams = {
                    page: 1,
                    pageSize: filterParams?.pageSize || 10,
                    // searchWord: searchWord,
                    sortBy: Direction.ASC,
                    // searchColum: searchColumnTemp || searchColumn,
                };
                const newPayload = removeUndefinedAttribute(queryParams);
                onFilterData(newPayload);
            } catch (error) {
                // Handle error
            }
        },
        [filterParams]
    );

    const handleSearchData = (value: string | undefined) => {
        onSearchKeyword(value);
        setSearchWord(value);
    };

    const onSearchKeyword = React.useMemo(() => {
        return debouce(onSearchData, 600);
    }, [onSearchData]);

    React.useEffect(() => {
        return () => {
            onSearchKeyword.cancel();
        };
    }, [onSearchKeyword]);
    // end handle search keyword

    const onEditTenant = React.useCallback(
        async (tenant: TTenants) => {
            if (!tenant.id) {
                return;
            }
            try {
                dispatch(setLoading(true));
                const response = await getTenantDetail(tenant.id);
                if (response.status === 200) {
                    setTenantSelected(response.data.data);
                    setShowTenantDialog(true);
                }
            } catch (error) {
                // Handle error
            }
            finally {
                dispatch(setLoading(false));
            }
        },
        [setTenantSelected]
    );

    const columns: ColumnsType<TTenants> = [
        {
            title: 'Action',
            dataIndex: 'tenantId',
            key: 'tenantId',
            width: '40px',
            render: (tenantId: string, data: TTenants) => {
                return (
                    <ActionMenu>
                        <IconButton
                            icon={<ICMaterialSymbol icon="edit_square" />}
                            btnType="tertiary"
                            onClick={() => {
                                onEditTenant(data);
                            }}
                        />
                    </ActionMenu>
                );
            },
        },
        {
            title: 'Name',
            dataIndex: 'name',
            field: 'name',
            key: 'name',
            sorter: true,
            render: (title: string) => {
                return <TitleRenderer title={title} />;
            },
        },
        {
            title: 'TIN',
            dataIndex: 'tin',
            field: 'tin',
            key: 'tin',
            sorter: false,
        },
        {
            title: 'Registration No',
            dataIndex: 'registration_no',
            field: 'registration_no',
            key: 'registration_no',
            sorter: false,
        },
        {
            title: 'SST Registration No ID',
            dataIndex: 'sst_registration_no_id',
            field: 'sst_registration_no_id',
            key: 'sst_registration_no_id',
            sorter: false,
        },
        {
            title: 'SST Registration No Type',
            dataIndex: 'sst_registration_no_type',
            field: 'sst_registration_no_type',
            key: 'sst_registration_no_type',
            sorter: false,
        },
        {
            title: 'Tourism Tax No',
            dataIndex: 'tourism_tax_no',
            field: 'tourism_tax_no',
            key: 'tourism_tax_no',
            sorter: false,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            field: 'email',
            key: 'email',
            sorter: false,
        },
        {
            title: 'MSIC',
            dataIndex: 'msic',
            field: 'msic',
            key: 'msic',
            sorter: false,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            field: 'description',
            key: 'description',
            sorter: false,
        },
        {
            title: 'Address',
            dataIndex: 'address',
            field: 'address',
            key: 'address',
            sorter: false,
            render: (address: any) => {
                return (
                    <div className="flex gap-1">
                        <div>{address.line0}</div>
                        <div>{address.line1}</div>
                        <div>{address.line2}</div>
                        <div>{address.postal}</div>
                        <div>{address.city}</div>
                        <div>{address.state}</div>
                        <div>{address.country}</div>
                    </div>
                );
            },
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            field: 'phone',
            key: 'phone',
            sorter: false,
        },
    ].map((col: ColumnType<TTenants>) => {
        if (col.sorter) {
            const oldTitle = col.title;
            col.title = ({ sortColumns }) => {
                const sortedColumn = sortColumns?.find(
                    ({ column }) => column.key === col.key
                );
                return (
                    <SortableColumn
                        title={oldTitle as string}
                        order={sortedColumn?.order}
                        name={col.dataIndex as string}
                    />
                );
            };
        } else {
            col.title = (
                <div className="text-body-bold text-high-em">
                    {col.title as string}
                </div>
            );
        }
        col.showSorterTooltip = false;
        return col;
    });

    const doSaveTenant = React.useCallback(
        async (
            tenantId: number | string | undefined,
            payload: TTenantRequest
        ) => {
            try {
                dispatch(setLoading(true));
                if (tenantId) {
                    const result = await updateTenant(tenantId, payload);
                    if (result.status === 200) {
                        showSuccess(
                            'Save Tenant success',
                            'Tenant has been updated'
                        );
                    }
                } else {
                    const result = await createTenant(payload);
                    if (result.status === 200) {
                        showSuccess(
                            'Save Tenant success',
                            'Tenant has been created'
                        );
                    }
                }
                onReload();
                setShowTenantDialog(false);
            } catch (error: any) {
                const errorMessage =
                    error?.response?.data?.detail ||
                    'Please check your input data';
                showError('Save Tenant failed', errorMessage);
            } finally {
                dispatch(setLoading(false));
            }
        },
        [tenantList, dispatch, showSuccess, showError]
    );

    const onSaveTenants = React.useCallback(() => {
        form.validateFields()
            .then(values => {
                const tenantParams: TTenantRequest = {
                    id: tenantSelected?.id,
                    name: values.name,
                    tin: values.tin,
                    registration_no: values.registration_no,
                    sst_registration_no_id: values.sst_registration_no_id,
                    sst_registration_no_type: values.sst_registration_no_type,
                    tourism_tax_no: values.tourism_tax_no,
                    email: values.email,
                    msic: values.msic,
                    description: values.description,
                    address: {
                        line0: values.line0,
                        line1: values.line1,
                        line2: values.line2,
                        postal: values.postal,
                        city: values.city,
                        state: values.state,
                        country: values.country,
                    },
                    phone: values.phone,
                    invoice_api_settings: {
                        api_url: values.api_url,
                        username: values.username,
                        password: values.password,
                        api_key: values.invoice_api_settings_api_key,
                        api_signature: values.api_signature,
                    },
                    api_key: values.api_key,
                };
                if (tenantSelected?.id) {
                    doSaveTenant(tenantSelected.id, tenantParams);
                } else {
                    doSaveTenant(undefined, tenantParams);
                }
            })
            .catch(() => {
                showError('Save Tenant failed', 'Please check your input data');
            });
    }, [form, tenantList, tenantSelected, showError]);

    return (
        <WrapperTable className="flex flex-col gap-[20px] w-full overflow-hidden h-full items-start box-border">
            <div className="flex w-full px-[12px] py-[20px] shadow-e-03 max-md:justify-center items-center max-md:flex-wrap gap-3 justify-between bg-white rounded-[12px]">
                <div className="flex gap-3 items-center justify-center w-full flex-wrap">
                    <WrapperInput className="flex items-center w-full gap-2">
                        <Input
                            placeholder="Search by ..."
                            value={searchWord}
                            onChange={e => handleSearchData(e.target.value)}
                            style={{
                                width: '100%',
                            }}
                            size="large"
                        />
                    </WrapperInput>
                </div>
                <WrapperButton className="flex gap-[10px]">
                    <EInvoiceButton
                        btnType="primary"
                        onClick={() => {
                            setTenantSelected(undefined);
                            setShowTenantDialog(true);
                        }}
                        style={{
                            width: '220px',
                        }}
                        btnSize="md"
                    >
                        <div className="flex space-x-2 items-center">
                            <ICAdd stroke="#fff" width={18} height={18} />
                            <span>Create new Tenant</span>
                        </div>
                    </EInvoiceButton>
                </WrapperButton>
            </div>
            <div
                className={classNames([
                    'flex flex-col w-full h-full rounded-[12px] overflow-hidden shadow-e-03',
                    !mobileMode && 'bg-white',
                ])}
            >
                <EcoScrollTable totalPages={pagination?.totalCount} />
                <KeywordContext.Provider value={keyword ?? ''}>
                    <Table
                        key="tenant-list"
                        className={classNames('white-header w-full')}
                        columns={columns}
                        dataSource={tenantList}
                        rowClassName="cursor-pointer data-row"
                        rowKey="id"
                        scroll={{ x: 'max-content' }}
                        locale={{
                            emptyText: (
                                <EmptyTable
                                    keyword={keyword}
                                    onCreateNew={() => {
                                        setTenantSelected(undefined);
                                        setShowTenantDialog(true);
                                    }}
                                />
                            ),
                        }}
                        pagination={false}
                    />
                </KeywordContext.Provider>
                {pagination?.totalPage && pagination.totalPage > 1 ? (
                    <Row
                        gutter={16}
                        style={{
                            margin: 0,
                        }}
                        className={classNames([
                            'grid items-center max-md:justify-center justify-end w-full bg-white p-[16px] border-t border-solid border-outline-med rounded-b-[12px]',
                            'max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:z-10 max-md:rounded-none',
                        ])}
                    >
                        <EInvoicePagination
                            onChange={onChangePage}
                            defaultCurrent={pagination?.page}
                            defaultPageSize={pagination?.pageSize}
                            total={pagination?.totalCount}
                            currentPage={pagination?.page}
                            showSizeChanger={false}
                        />
                    </Row>
                ) : null}
            </div>

            <Modal
                centered
                onCancel={() => setShowTenantDialog(false)}
                open={showTenantDialog}
                maskClosable={false}
                destroyOnClose
                width={1000}
                afterClose={() => form.resetFields()}
                title={
                    <div className="font-bold text-[18px]">
                        {tenantSelected ? 'Edit Tenant' : 'New Tenant Register'}
                    </div>
                }
                footer={
                    <div className="grid grid-cols-[auto_auto] justify-end gap-[16px]">
                        <EInvoiceButton
                            btnType="sub"
                            btnSize="sm"
                            onClick={() => setShowTenantDialog(false)}
                        >
                            Cancel
                        </EInvoiceButton>
                        <EInvoiceButton
                            btnType="primary"
                            btnSize="sm"
                            onClick={onSaveTenants}
                        >
                            Save
                        </EInvoiceButton>
                    </div>
                }
            >
                <div className="flex flex-col gap-[20px] w-full overflow-hidden h-full items-start box-border">
                    <TenantForm tenantData={tenantSelected} form={form} />
                </div>
            </Modal>
        </WrapperTable>
    );
};

type IEmptyTableProps = {
    keyword: string | undefined;
    onCreateNew: () => void;
};

const EmptyTable: React.FC<IEmptyTableProps> = ({ keyword, onCreateNew }) => {
    return (
        <div className="my-6 flex flex-col gap-[5px]">
            <div className="flex justify-center">
                <ICNoneData />
            </div>
            <div className="text-standard-bold text-high-em">
                {keyword
                    ? `No data found with keyword “${keyword}”`
                    : 'No data found'}
            </div>
            <div className="flex justify-center">
                <span>
                    <EInvoiceButton
                        btnType="outline"
                        btnSize="sm"
                        style={{
                            borderRadius: '144px',
                        }}
                        onClick={() => onCreateNew()}
                    >
                        <div className="flex space-x-2 items-center">
                            <ICAdd stroke="#7357FF" width={18} height={18} />
                            <span>Create new Tenant</span>
                        </div>
                    </EInvoiceButton>
                </span>
            </div>
        </div>
    );
};
